import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useState, useEffect, useCallback, useRef } from 'react';

import testimonialQuoteIcon from '@/assets/TestimonialSection/caption-icon.svg';
import johnImage from '@/assets/TestimonialSection/john.webp';
import janeImage from '@/assets/TestimonialSection/jane.webp';
import samanthaImage from '@/assets/TestimonialSection/samantha.webp';
import Section from '../../../components/Section/section';
import { useInView } from '../../../hooks/useInView';
import { color } from '@/themes/color';
import { typography } from '@/themes/typography';

const CARD_STAGGER_MS = 120;

const TESTIMONIALS = [
  {
    name: 'Oz Clarke, OBE',
    location: 'British wine writer, actor, TV presenter and broadcaster',
    quote:
      "At last! An idea whose time has come. Yes Please! A Guide to the Vineyards of England and Wales. But how to find them? How to travel to them? What will we discover when we get there - a gentle rural idyll or a full-blown tourism experience? The UK Vineyard Guide is here to make visiting the vines as easy and as pleasurable as can be. Summer's coming.... The vines are beckoning.... Follow their call!",
    image: johnImage,
    imageAlt: 'Oz Clarke, OBE',
  },
  {
    name: 'Ellie Scott DipWSET, SWS ',
    location: 'Wine writer and consultant',
    quote:
      'The UK wine industry is at such an exciting stage, with so much to be discovered across the country. UK Vineyard Guide is a much needed tool to highlight tourism and hospitality options around our beautiful vineyards and bring everything together in one place.',
    image: janeImage,
    imageAlt: 'Ellie Scott',
  },
  {
    name: 'Archie Patel',
    location: 'Wine & Travel Communicator & Content Creator',
    quote:
      'This is the app England has been waiting for and will totally change the face of English wine tourism',
    image: samanthaImage,
    imageAlt: 'Archie Patel',
  },
];

export default function TestimonialSection(): JSX.Element {
  const [sectionRef, inView] = useInView({
    threshold: 0.2,
    unobserveOnce: true,
  });

  useEffect(() => {
    if (inView) {
      gtag('event', 'section_view', {
        event_category: 'Engagement',
        event_label: 'Testimonial Section',
      });
    }
  }, [inView]);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [, setTouchEnd] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia('(max-width: 899px)').matches
      : false
  );
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 899px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const minSwipeDistance = 50;

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0]?.clientX ?? null);
  };

  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrentCardIndex((prev) => (prev + 1) % TESTIMONIALS.length);
    }, 5000);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    startTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isMobile, startTimer]);

  const goToCard = useCallback(
    (index: number) => {
      setCurrentCardIndex(index);
      if (isMobile) startTimer();
    },
    [isMobile, startTimer]
  );

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStart == null) return;

    const clientX = e.changedTouches[0]?.clientX;
    if (clientX == null) return;

    setTouchEnd(clientX);

    const distance = touchStart - clientX;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      goToCard((currentCardIndex + 1) % TESTIMONIALS.length);
    } else if (isRightSwipe) {
      goToCard(
        (currentCardIndex - 1 + TESTIMONIALS.length) % TESTIMONIALS.length
      );
    }
  };

  return (
    <Section
      ref={sectionRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: color.cream['100'],
        py: { xs: '48px', md: '64px' },
        px: { xs: '24px', md: '64px' },
      }}
    >
      <style>{`
        @keyframes testimonialCardIn {
          from {
            opacity: 0;
            transform: translateY(24px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes dotProgress {
          0% { width: 0%; }
          100% { width: 100%; }
        }
      `}</style>
      <Typography
        component="h2"
        sx={{
          ...typography.display,
          color: color.charcoal.default,
          textAlign: 'center',
          marginBottom: { xs: '40px', md: '64px' },
          fontSize: {
            xs: 'var(--font-size-36, 36px)',
            sm: 'var(--font-size-48, 48px)',
            md: typography.display.fontSize,
          },
        }}
      >
        Loved by explorers and
        <br />
        wine enthusiasts alike
      </Typography>

      <Box
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: '24px', md: '32px' },
          width: '100%',
          touchAction: 'pan-y',
          height: { xs: '460px', md: 'auto' },
        }}
      >
        {TESTIMONIALS.map((t, i) => (
          <Box
            key={t.name}
            sx={{
              flex: 1,
              minWidth: 0,
              backgroundColor: '#fff',
              borderRadius: '16px',
              height: { xs: '260px', md: '250px' },
              boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
              p: '24px',
              display: {
                xs: i === currentCardIndex ? 'flex' : 'none',
                md: 'flex',
              },
              flexDirection: 'column',
              justifyContent: 'flex-start',
              willChange: 'transform, opacity',
              opacity: { xs: 1, md: 0 },
              transform: 'translateY(24px)',
              ...(inView && {
                animation: 'testimonialCardIn 0.6s ease-out forwards',
                animationDelay: `${i * CARD_STAGGER_MS}ms`,
              }),
              '@media (max-width: 899px)': {
                animation: 'none',
                opacity: 1,
              },
              touchAction: 'pan-y',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
              }}
            >
              <Box
                component="img"
                src={t.image}
                alt={t.imageAlt}
                loading="lazy"
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  objectFit: 'cover',
                  flexShrink: 0,
                  display: 'block',
                }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    ...typography.bodyText,
                    color: color.charcoal.default,
                    display: 'block',
                    fontSize: {
                      xs: 'var(--font-size-16, 16px)',
                      sm: 'var(--font-size-18, 18px)',
                      md: typography.bodyText.fontSize,
                    },
                  }}
                >
                  {t.name}
                </Typography>
                <Typography
                  sx={{
                    ...typography.caption,
                    color: color.charcoal['400'],
                    display: 'block',
                    marginTop: '4px',
                    fontSize: {
                      xs: 'var(--font-size-14, 14px)',
                      sm: 'var(--font-size-16, 16px)',
                      md: typography.caption.fontSize,
                    },
                  }}
                >
                  {t.location}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                gap: 8,
                marginTop: '16px',
              }}
            >
              <Box
                component="img"
                src={testimonialQuoteIcon}
                alt="quote"
                aria-hidden
                sx={{
                  marginBottom: '8px',
                }}
              />
              <Typography
                sx={{
                  ...typography.bodySmall,
                  fontWeight: 325,
                  color: color.charcoal.default,
                  display: 'block',
                  fontSize: {
                    xs: 'var(--font-size-18, 18px)',
                    sm: 'var(--font-size-20, 20px)',
                    md: typography.bodySmall.fontSize,
                  },
                }}
              >
                {t.quote}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          gap: '8px',
          justifyContent: 'center',
          alignItems: 'center',
          mt: { xs: '42px', md: '0px' },
        }}
      >
        {TESTIMONIALS.map((testimonial, index) => (
          <Box
            key={index}
            role="button"
            tabIndex={0}
            aria-label={`Go to ${testimonial.name} testimonial`}
            aria-pressed={index === currentCardIndex}
            onClick={() => goToCard(index)}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                goToCard(index);
              }
            }}
            sx={{
              width: index === currentCardIndex ? '40px' : '8px',
              height: '8px',
              borderRadius: '100px',
              backgroundColor: color.charcoal['200'],
              cursor: 'pointer',
              transition: 'width 0.3s ease',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            {index === currentCardIndex && (
              <Box
                key={currentCardIndex}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  height: '100%',
                  width: '0%',
                  backgroundColor: color.terracotta['300'],
                  borderRadius: '100px',
                  animation: 'dotProgress 5s linear forwards',
                }}
              />
            )}
          </Box>
        ))}
      </Box>
    </Section>
  );
}
